import QRCodeStyling from '@solana/qr-code-styling'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import type {
  CornerDotType,
  CornerSquareType,
  DotType,
  DrawType,
  ErrorCorrectionLevel,
  Mode,
  Options,
  TypeNumber,
} from '@solana/qr-code-styling'

import { Network } from '../types'
import { useDevice } from '../hooks'

interface Props {
  url: string
  network: Network
}

export default function QRCode({ url, network }: Props) {
  const { phone } = useDevice()

  const [size, setSize] = useState(() => (phone ? 250 : 300))

  /*
  useEffect(() => {
    const listener = () => setSize(Math.min(window.screen.availWidth - 48, 300))

    window.addEventListener('resize', listener)
    return () => window.removeEventListener('resize', listener)
  }, [])
*/
  const options = useMemo(
    () => createQROptions(url, network, size, 'transparent', '#fff'),
    [url, size, network],
  )

  const qr = useMemo(() => new QRCodeStyling(), [])

  useEffect(() => qr.update(options), [qr, options])

  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (ref.current) {
      qr.append(ref.current)
    }
  }, [ref, qr])

  return <div ref={ref} className="root flex justify-center" />
}

function createQROptions(
  url: string | URL,
  network: Network,
  size = 300,
  background = 'black',
  color = 'white',
): Options {
  let image = ''

  switch (network) {
    case Network.POLYGON:
      image = `data:image/svg+xml;utf8,<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
      viewBox="0 0 38.4 33.5"  xml:space="preserve">
   <g>
     <path d="M29,10.2c-0.7-0.4-1.6-0.4-2.4,0L21,13.5l-3.8,2.1l-5.5,3.3c-0.7,0.4-1.6,0.4-2.4,0L5,16.3
       c-0.7-0.4-1.2-1.2-1.2-2.1v-5c0-0.8,0.4-1.6,1.2-2.1l4.3-2.5c0.7-0.4,1.6-0.4,2.4,0L16,7.2c0.7,0.4,1.2,1.2,1.2,2.1v3.3l3.8-2.2V7
       c0-0.8-0.4-1.6-1.2-2.1l-8-4.7c-0.7-0.4-1.6-0.4-2.4,0L1.2,5C0.4,5.4,0,6.2,0,7v9.4c0,0.8,0.4,1.6,1.2,2.1l8.1,4.7
       c0.7,0.4,1.6,0.4,2.4,0l5.5-3.2l3.8-2.2l5.5-3.2c0.7-0.4,1.6-0.4,2.4,0l4.3,2.5c0.7,0.4,1.2,1.2,1.2,2.1v5c0,0.8-0.4,1.6-1.2,2.1
       L29,28.8c-0.7,0.4-1.6,0.4-2.4,0l-4.3-2.5c-0.7-0.4-1.2-1.2-1.2-2.1V21l-3.8,2.2v3.3c0,0.8,0.4,1.6,1.2,2.1l8.1,4.7
       c0.7,0.4,1.6,0.4,2.4,0l8.1-4.7c0.7-0.4,1.2-1.2,1.2-2.1V17c0-0.8-0.4-1.6-1.2-2.1L29,10.2z"/>
   </g>
   </svg>
   `
      break
    case Network.SOLANA:
      image = `data:image/svg+xml;utf8,<svg fill="${encodeURIComponent(
        color,
      )}" height="16" viewBox="0 0 16 14" width="16" xmlns="http://www.w3.org/2000/svg"><path d="m15.9176 11.038-2.6413 2.7713c-.0574.0602-.1269.1082-.2041.141s-.1604.0497-.2446.0497h-12.520966c-.059744 0-.118187-.0171-.168147-.0491-.0499596-.0321-.0892609-.0777-.1130745-.1313-.02381372-.0536-.03110249-.1129-.02097081-.1705.01013171-.0576.03724251-.111.07800141-.1538l2.6432769-2.7713c.05726-.06.12651-.1079.20346-.1407s.15996-.0498.2439-.05h12.52032c.0597 0 .1182.0171.1681.0492.05.032.0893.0776.1131.1313.0238.0536.0311.1128.021.1704-.0102.0576-.0373.1111-.078.1538zm-2.6413-5.58067c-.0574-.0602-.1269-.1082-.2041-.141s-.1604-.04971-.2446-.04966h-12.520966c-.059744 0-.118187.01708-.168147.04913-.0499596.03205-.0892609.07768-.1130745.13129-.02381372.0536-.03110249.11285-.02097081.17045.01013171.05761.03724251.11106.07800141.15379l2.6432769 2.77134c.05726.06004.12651.10794.20346.14073.07695.0328.15996.04979.2439.04993h12.52032c.0597 0 .1182-.01707.1681-.04913.05-.03205.0893-.07768.1131-.13129.0238-.0536.0311-.11285.021-.17045-.0102-.05761-.0373-.11106-.078-.15379zm-12.969666-1.99066h12.520966c.0842.00004.1674-.01687.2446-.04967s.1467-.0808.2041-.141l2.6413-2.771333c.0407-.042736.0678-.096189.078-.153792.0101-.057603.0028-.116847-.021-.170453s-.0631-.0992385-.1131-.1312911c-.0499-.0320526-.1084-.04912893-.1681-.0491309h-12.52032c-.08394.00013975-.16695.0171339-.2439.0499304s-.1462.0806976-.20346.1407366l-2.6425955 2.771333c-.0407196.04269-.0678184.09609-.07797306.15363-.01015467.05754-.00292373.11673.02080606.17031.0237297.05358.0629266.09922.1127835.13132.049857.03211.108207.04928.167893.04941z"/></svg>`
      break
  }

  return {
    type: 'svg' as DrawType,
    width: size,
    height: size,
    data: String(url),
    margin: 1,
    qrOptions: {
      typeNumber: 0 as TypeNumber,
      mode: 'Byte' as Mode,
      errorCorrectionLevel: 'L' as ErrorCorrectionLevel,
    },
    backgroundOptions: { color: background },
    dotsOptions: { type: 'dots' as DotType, color },
    cornersSquareOptions: {
      type: 'square' as CornerSquareType,
      color,
    },
    cornersDotOptions: { type: 'square' as CornerDotType, color },
    //imageOptions: { hideBackgroundDots: true, imageSize: 1.3, margin: 7 },
    //image: `data:image/svg+xml;base64,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`,
  }
}

/*
{
  "width": 300,
  "height": 300,
  "data": "https://qr-code-styling.com",
  "margin": 0,
  "qrOptions": {
    "typeNumber": "0",
    "mode": "Byte",
    "errorCorrectionLevel": "M"
  },
  "imageOptions": { "hideBackgroundDots": true, "imageSize": 0.4, "margin": 0 },
  "dotsOptions": { "type": "square", "color": "#000000" },
  "backgroundOptions": { "color": "#ffffff" },
  "image": "10cc19bd484118dbcd0a7886a38ceddc.png",
  "dotsOptionsHelper": {
    "colorType": { "single": true, "gradient": false },
    "gradient": {
      "linear": true,
      "radial": false,
      "color1": "#6a1a4c",
      "color2": "#6a1a4c",
      "rotation": "0"
    }
  },
  "cornersSquareOptions": { "type": "square", "color": "#000000" },
  "cornersSquareOptionsHelper": {
    "colorType": { "single": true, "gradient": false },
    "gradient": {
      "linear": true,
      "radial": false,
      "color1": "#000000",
      "color2": "#000000",
      "rotation": "0"
    }
  },
  "cornersDotOptions": { "type": "", "color": "#000000" },
  "cornersDotOptionsHelper": {
    "colorType": { "single": true, "gradient": false },
    "gradient": {
      "linear": true,
      "radial": false,
      "color1": "#000000",
      "color2": "#000000",
      "rotation": "0"
    }
  },
  "backgroundOptionsHelper": {
    "colorType": { "single": true, "gradient": false },
    "gradient": {
      "linear": true,
      "radial": false,
      "color1": "#ffffff",
      "color2": "#ffffff",
      "rotation": "0"
    }
  }
}

*/

/*


{
  "width": 300,
  "height": 300,
  "data": "https://qr-code-styling.com",
  "margin": 0,
  "qrOptions": {
    "typeNumber": "0",
    "mode": "Byte",
    "errorCorrectionLevel": "Q"
  },

  "imageOptions": { "hideBackgroundDots": true, "imageSize": 0.4, "margin": 0 },
  "dotsOptions": { "type": "dots", "color": "#000000", "gradient": null },
  "backgroundOptions": { "color": "#ffffff" },
  "image": "10cc19bd484118dbcd0a7886a38ceddc.png",
  "dotsOptionsHelper": {
    "colorType": { "single": true, "gradient": false },
    "gradient": {
      "linear": true,
      "radial": false,
      "color1": "#6a1a4c",
      "color2": "#6a1a4c",
      "rotation": "0"
    }
  },
  "cornersSquareOptions": { "type": "extra-rounded", "color": "#000000" },
  "cornersSquareOptionsHelper": {
    "colorType": { "single": true, "gradient": false },
    "gradient": {
      "linear": true,
      "radial": false,
      "color1": "#000000",
      "color2": "#000000",
      "rotation": "0"
    }
  },
  "cornersDotOptions": { "type": "", "color": "#000000" },
  "cornersDotOptionsHelper": {
    "colorType": { "single": true, "gradient": false },
    "gradient": {
      "linear": true,
      "radial": false,
      "color1": "#000000",
      "color2": "#000000",
      "rotation": "0"
    }
  },
  "backgroundOptionsHelper": {
    "colorType": { "single": true, "gradient": false },
    "gradient": {
      "linear": true,
      "radial": false,
      "color1": "#ffffff",
      "color2": "#ffffff",
      "rotation": "0"
    }
  }
}

*/
